import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import "ag-grid-enterprise";
import AgGridTable from "../utilities/AgGridTable";
import ListPageHeader from "../utilities/ListPageHeader";
import { Button, CircularProgress,Box } from "@mui/material";
import OAuthService from "src/app/auth/services/oAuthService";
import oAuthServiceConfig from "src/app/auth/services/oAuthService/oAuthServiceConfig";
import EuroIcon from "@mui/icons-material/Euro";
import CustomDatePicker from "../utilities/CustomDatePicker";

const currentYear = new Date().getFullYear();
const defaultStartDate = `${currentYear}-01-01`;
const defaultEndDate = `${currentYear}-12-31`;

const initialValue = {
  reporter: ["DE", "EU27_2020"],
  product: ["94034010", "94035000"],
  startDate: defaultStartDate,
  endDate: defaultEndDate,
  startMonthRange: 1,
  startYearRange: currentYear,
  endMonthRange: 12,
  endYearRange: currentYear,
  countryPartner: [""],
};

const monthNames = {
  1: "January", 2: "February", 3: "March", 4: "April",
  5: "May", 6: "June", 7: "July", 8: "August",
  9: "September", 10: "October", 11: "November", 12: "December",
};

const validateApiResponse = (res) => {
  if (res?.data?.errorCode || res?.data?.errorMessage) {
    throw new Error(res.data.errorMessage || "Unknown API Error");
  }
  if (!Array.isArray(res?.data) && res?.data?._embedded) {
    return res.data; 
  }
  if (!Array.isArray(res?.data)) {
    return []; 
  }
  return res.data;
};

const EurostatComparison = () => {
  const euroStatUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.euroStatCustomDS}`;
  const countryUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}${oAuthServiceConfig.country}`;
  
  const nolteSalesUrl = `${process.env.REACT_APP_RESOURCE_API_KEY_URL}/api/get-customer-monthly-sales-by-date-range`;

  const gridRef = useRef();
  const isMounted = useRef(true);
  const [rowData, setRowData] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [euroStatData, setEuroStatData] = useState(initialValue);
  const [gridApi, setGridApi] = useState(null);
  const [gridColumnApi, setGridColumnApi] = useState(null);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: true,
    enableValue: true,
    enableRowGroup: true,
    enablePivot: true,
  }), []);

  const autoGroupColumnDef = useMemo(() => ({
    headerName: "Group",
    minWidth: 250,
    width: 250,
    pinned: 'left',
    suppressSizeToFit: true,
  }), []);

  const formatNumber = useCallback((value) => {
    if (value == null || Number.isNaN(value) || value === 0) return 0;
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(value);
  }, []);

  const percentageCellRenderer = useCallback((props) => {
    const formatted = formatNumber(props.value);
    return (
      <span className="flex items-center">
        <p>{`${formatted}%`}</p>
      </span>
    );
  }, [formatNumber]);

  const currencyCellRenderer = useCallback((props) => {
    const formatted = formatNumber(props.value);
    return (
      <span className="flex items-center">
        <EuroIcon style={{ fontSize: "14px", marginInline: "5px" }} />
        <p>{formatted}</p>
      </span>
    );
  }, [formatNumber]);

  const columnDefs = useMemo(() => [
    { field: "partner", rowGroup: true, hide: true },
    { field: "year", minWidth: 50, pivot: true, rowGroup: true, hide: true },
    { 
      field: "month", 
      minWidth: 50, 
      rowGroup: true, 
      valueGetter: (params) => (params.data ? monthNames[params.data.month] : ""), 
      hide: true 
    },
    { field: "product", minWidth: 150 },
    { 
      field: "germanyExportValue", headerName: "Germany", minWidth: 180, aggFunc: "sum", 
      valueFormatter: (p) => formatNumber(p.value), cellRenderer: currencyCellRenderer 
    },
    { 
      field: "europeExportValue", headerName: "Europe", minWidth: 180, aggFunc: "sum",
      valueFormatter: (p) => formatNumber(p.value), cellRenderer: currencyCellRenderer 
    },
    { 
      field: "restOfEU", headerName: "Rest Of EU", minWidth: 180, aggFunc: "sum",
      valueFormatter: (p) => formatNumber(p.value), cellRenderer: currencyCellRenderer 
    },
    { 
      field: "germanyVsEurope", headerName: "Germany Vs Europe", minWidth: 220,
      valueFormatter: (p) => formatNumber(p.value), cellRenderer: percentageCellRenderer 
    },
    { 
      field: "nolteFZESales", headerName: "Nolte FZE", minWidth: 220, aggFunc: "sum",
      valueFormatter: (p) => formatNumber(p.value), cellRenderer: currencyCellRenderer 
    },
    { 
      field: "nolteFZEvsGermany", headerName: "NolteFZE Vs Germany", minWidth: 240, aggFunc: "sum",
      valueFormatter: (p) => formatNumber(p.value), cellRenderer: percentageCellRenderer 
    },
    { 
      field: "nolteFZEvsEU", headerName: "NolteFZE Vs EU", minWidth: 220, aggFunc: "sum",
      valueFormatter: (p) => formatNumber(p.value), cellRenderer: percentageCellRenderer 
    },
  ], [currencyCellRenderer, percentageCellRenderer, formatNumber]);

  const processCombinedData = useCallback((euroStat, nolteSales) => {
    const europe = euroStat.filter((data) => data.reporter === "European Union - 27 countries");
    const germany = euroStat.filter((data) => data.reporter === "Germany");

    const europeMap = new Map();
    europe.forEach(eu => {
      europeMap.set(`${eu.month}-${eu.year}-${eu.partner}-${eu.product}`, eu.exportValue || 0);
    });

    const nolteMap = new Map();
    if (nolteSales && nolteSales.length > 0) {
      nolteSales.forEach(sale => {
        if (sale.category === "RET" || sale.category === "PRJ") {
          const key = `${sale.month}-${sale.year}-${sale.country}`;
          nolteMap.set(key, (nolteMap.get(key) || 0) + (sale.total || 0));
        }
      });
    }

    const assignedMonthlyTotals = new Set();

    return germany.map((ger) => {
      const europeExportValue = europeMap.get(`${ger.month}-${ger.year}-${ger.partner}-${ger.product}`) || 0;
      const germanyExportValue = ger.exportValue || 0;
      const restOfEUValue = Math.max(0, europeExportValue - germanyExportValue);
      
      let germanyNolteSales = 0;
      const monthlyKey = `${ger.month}-${ger.year}-${ger.partner}`;
      
      if (nolteMap.has(monthlyKey) && !assignedMonthlyTotals.has(monthlyKey)) {
        germanyNolteSales = nolteMap.get(monthlyKey);
        assignedMonthlyTotals.add(monthlyKey);
      }

      return {
        month: ger.month,
        year: ger.year,
        partner: ger.partner,
        product: ger.product,
        germanyExportValue,
        europeExportValue,
        restOfEU: restOfEUValue,
        germanyVsEurope: europeExportValue === 0 ? 0 : (germanyExportValue / europeExportValue) * 100,
        nolteFZESales: germanyNolteSales,
        nolteFZEvsGermany: germanyExportValue === 0 ? 0 : (germanyNolteSales / germanyExportValue) * 100,
        nolteFZEvsEU: europeExportValue === 0 ? 0 : (germanyNolteSales / europeExportValue) * 100
      };
    });
  }, []);

  const fetchCombinedData = useCallback(async (payload) => {
    setIsLoading(true);
    setError("");
    
    if (gridRef.current?.api) {
      gridRef.current.api.showLoadingOverlay();
    }

    try {
      const { startDate, endDate, ...euroStatPayload } = payload;
      const nolteGetUrl = `${nolteSalesUrl}?from=${startDate}&to=${endDate}&dateType=ACTUAL`;

      const [euroRes, nolteRes] = await Promise.all([
        OAuthService.addData(`${euroStatUrl}?sort=id,asc`, euroStatPayload),
        OAuthService.getList(nolteGetUrl).catch(() => ({ data: [] }))
      ]);

      if (!isMounted.current) return;

      const validEuroStat = validateApiResponse(euroRes);
      const validNolte = validateApiResponse(nolteRes);

      const processedData = processCombinedData(validEuroStat, validNolte);
      setRowData(processedData);

      if (gridRef.current?.api) {
        processedData.length === 0 
          ? gridRef.current.api.showNoRowsOverlay() 
          : gridRef.current.api.hideOverlay();
      }
    } catch (err) {
      if (isMounted.current) {
        setError(err.message);
        setRowData([]);
        if (gridRef.current?.api) {
          gridRef.current.api.showNoRowsOverlay();
        }
      }
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [euroStatUrl, nolteSalesUrl, processCombinedData]);

  const getCountries = useCallback(async () => {
    try {
      const res = await OAuthService.getList(`${countryUrl}?sort=name,asc&size=1000`);
      if (!isMounted.current) return;
      
      const countries = res.data?._embedded?.countries || [];
      const isoCodes = countries.filter(i => i?.name).map(c => c.isoCode);
      
      const defaultModel = { ...initialValue, countryPartner: isoCodes };
      setEuroStatData(defaultModel);
      fetchCombinedData(defaultModel);
    } catch (err) {
      if (isMounted.current) setError(err.message);
    }
  }, [countryUrl, fetchCombinedData]);

  useEffect(() => {
    isMounted.current = true;
    getCountries();
    return () => { isMounted.current = false; };
  }, [getCountries]);

  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  }, []);

  useEffect(() => {
    if (!gridApi || rowData.length === 0) return;

    const timer = setTimeout(() => {
      try {
        const groupCol = gridColumnApi 
          ? gridColumnApi.getColumn("ag-Grid-AutoColumn") 
          : gridApi.getColumn("ag-Grid-AutoColumn");

        if (groupCol) {
          if (gridColumnApi && gridColumnApi.setColumnWidth) {
            gridColumnApi.setColumnWidth(groupCol, 250);
          } else if (gridApi && gridApi.setColumnWidth) {
            gridApi.setColumnWidth(groupCol, 250);
          }
        }
      } catch (e) {
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [gridApi, gridColumnApi, rowData]);

  const getGroupRowAgg = useCallback((params) => {
    const result = {
      germanyExportValue: 0, europeExportValue: 0, restOfEU: 0,
      germanyVsEurope: 0, nolteFZESales: 0, nolteFZEvsGermany: 0, nolteFZEvsEU: 0,
    };

    params.nodes.forEach((node) => {
      const data = node.group ? node.aggData : node.data;
      if (typeof data.germanyExportValue === "number") result.germanyExportValue += data.germanyExportValue;
      if (typeof data.europeExportValue === "number") result.europeExportValue += data.europeExportValue;
      if (typeof data.restOfEU === "number") result.restOfEU += data.restOfEU;
      if (typeof data.nolteFZESales === "number") result.nolteFZESales += data.nolteFZESales;
    });

    result.restOfEU = Math.max(0, result.europeExportValue - result.germanyExportValue);
    result.germanyVsEurope = result.europeExportValue === 0 ? 0 : (result.germanyExportValue / result.europeExportValue) * 100;
    result.nolteFZEvsGermany = result.germanyExportValue === 0 ? 0 : (result.nolteFZESales / result.germanyExportValue) * 100;
    result.nolteFZEvsEU = result.europeExportValue === 0 ? 0 : (result.nolteFZESales / result.europeExportValue) * 100;

    return result;
  }, []);

  const handleChange = useCallback((event, key) => {
    let dateObj = event instanceof Date ? event : new Date(event);

    if (Number.isNaN(dateObj.getTime()) && typeof event === "string") {
      const parts = event.split("-");
      if (parts.length === 3) {
        dateObj = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
      }
    }

    if (Number.isNaN(dateObj.getTime())) return;

    const year = dateObj.getFullYear();
    const month = dateObj.getMonth() + 1;
    const formattedDate = `${year}-${String(month).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;

    setEuroStatData((prev) => ({
      ...prev,
      [key]: formattedDate,
      [key === "startDate" ? "startMonthRange" : "endMonthRange"]: month,
      [key === "startDate" ? "startYearRange" : "endYearRange"]: year,
    }));
  }, []);

 const headerInputFields = useMemo(() => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "center" },
        gap: { xs: 1.5, md: 3 },
        py: 0.5,
        width: { xs: "100%", md: "auto" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          justifyContent: "space-between",
        }}
      >
        <label className="text-md whitespace-nowrap" style={{ minWidth: 80 }}>
          Start Date:
        </label>
        <Box sx={{ width: { xs: "100%", md: 150 }, flex: { xs: 1, md: "0 0 auto" } }}>
          <CustomDatePicker
            className="w-full"
            format={"dd-MM-yyyy"}
            name={"startDate"}
            value={euroStatData.startDate}
            handleChange={(e) => handleChange(e, "startDate")}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          justifyContent: "space-between",
        }}
      >
        <label className="text-md whitespace-nowrap" style={{ minWidth: 80 }}>
          End Date:
        </label>
        <Box sx={{ width: { xs: "100%", md: 150 }, flex: { xs: 1, md: "0 0 auto" } }}>
          <CustomDatePicker
            className="w-full"
            format={"dd-MM-yyyy"}
            name={"endDate"}
            value={euroStatData.endDate}
            handleChange={(e) => handleChange(e, "endDate")}
          />
        </Box>
      </Box>

      <Button
        variant="contained"
        color="secondary"
        fullWidth={false}
        sx={{ height: 36, width: { xs: "100%", md: "auto" } }}
        onClick={() => fetchCombinedData(euroStatData)}
        disabled={isLoading}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
      </Button>
    </Box>
  );
}, [euroStatData, isLoading, handleChange, fetchCombinedData]);
  return (
    <>
      <ListPageHeader
        componentName="EurostatComparison"
        headerInputFields={headerInputFields}
        pagination={false} pageName="Eurostat Comparison" error={error} gridRef={gridRef}
      />
      <div className="h-full">
        <AgGridTable
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          getGroupRowAgg={getGroupRowAgg}
          onGridReady={onGridReady}
          columnFilter={"agMultiColumnFilter"}
          filterValueGetter={"partner"}
          hideCheckbox={true}
          editable={false}
          suppressRowCount={true}
          suppressAggFuncInHeader={true}
        />
      </div>
    </>
  );
};

export default EurostatComparison;